// DAS-2195: Cron Schedule Editor — modal to edit a script's cron schedule
// Includes validation, human-readable preview, and confirmation step.
import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Input, Button, message, Alert } from 'antd';
import api from '../functions/api';

const FIELD_NAMES = ['minute', 'hour', 'day of month', 'month', 'day of week'];
const FIELD_RANGES = [[0, 59], [0, 23], [1, 31], [1, 12], [0, 7]];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const CRON_FIELD_RE = /^(\*(\/\d+)?|\d+(-\d+)?(\/\d+)?(,\d+(-\d+)?(\/\d+)?)*)$/;

/**
 * Validate a single cron field. Returns error string or null.
 */
function validateField(value, fieldIdx) {
  const name = FIELD_NAMES[fieldIdx];
  const [lo, hi] = FIELD_RANGES[fieldIdx];

  if (!CRON_FIELD_RE.test(value)) {
    return `Invalid characters in ${name}: "${value}"`;
  }
  const nums = value.match(/\d+/g) || [];
  for (const n of nums) {
    const v = parseInt(n, 10);
    if (v < lo || v > hi) {
      return `${name} value ${v} out of range (${lo}–${hi})`;
    }
  }
  return null;
}

/**
 * Validate a full 5-field cron expression. Returns array of error strings.
 */
function validateCron(expr) {
  const errors = [];
  const trimmed = expr.trim();

  if (!trimmed) {
    errors.push('Cron expression is empty');
    return errors;
  }

  // Check for multiple consecutive spaces
  if (/  +/.test(trimmed)) {
    errors.push('Too many spaces — use exactly one space between fields');
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) {
    errors.push(`Must have exactly 5 fields (minute hour day month weekday), got ${parts.length}`);
    return errors;
  }

  for (let i = 0; i < 5; i++) {
    const err = validateField(parts[i], i);
    if (err) errors.push(err);
  }

  // Dangerous: runs every minute
  if (parts[0] === '*' && parts[1] === '*') {
    errors.push('This schedule would run every minute — not allowed');
  }

  return errors;
}

/**
 * Convert a cron expression to human-readable text.
 */
function cronToHuman(expr) {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minute, hour, dom, month, dow] = parts;

  const segments = [];

  // Minute
  if (minute === '*') {
    segments.push('every minute');
  } else if (minute.startsWith('*/')) {
    segments.push(`every ${minute.slice(2)} minutes`);
  } else {
    segments.push(`at minute ${minute}`);
  }

  // Hour
  if (hour === '*') {
    segments.push('of every hour');
  } else if (hour.startsWith('*/')) {
    segments.push(`every ${hour.slice(2)} hours`);
  } else {
    const hours = hour.split(',').map(h => `${h.padStart(2, '0')}:${minute === '*' ? '00' : minute.padStart(2, '0')}`);
    // Override minute segment for cleaner output
    if (minute !== '*' && !minute.startsWith('*/')) {
      segments.length = 0; // reset
      segments.push(`at ${hours.join(', ')}`);
    }
  }

  // Day of week
  if (dow !== '*') {
    const days = dow.split(',').map(d => {
      const n = parseInt(d, 10);
      return DAY_NAMES[n % 7] || d;
    });
    segments.push(`on ${days.join(', ')}`);
  }

  // Day of month
  if (dom !== '*') {
    segments.push(`on day ${dom} of the month`);
  }

  // Month
  if (month !== '*') {
    const months = month.split(',').map(m => {
      const n = parseInt(m, 10);
      return MONTH_NAMES[n] || m;
    });
    segments.push(`in ${months.join(', ')}`);
  }

  return segments.join(' ');
}

const CronScheduleEditor = ({ visible, onClose, scriptName, currentSchedule, onSuccess }) => {
  const [value, setValue] = useState(currentSchedule || '');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset state when modal opens
  React.useEffect(() => {
    if (visible) {
      setValue(currentSchedule || '');
      setConfirming(false);
      setSaving(false);
    }
  }, [visible, currentSchedule]);

  const errors = useMemo(() => validateCron(value), [value]);
  const isValid = errors.length === 0;
  const humanReadable = useMemo(() => isValid ? cronToHuman(value.trim()) : null, [value, isValid]);

  const handleNext = useCallback(() => {
    if (isValid) setConfirming(true);
  }, [isValid]);

  const handleBack = useCallback(() => {
    setConfirming(false);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await api.post('/cron_schedule', {
        script_name: scriptName,
        new_schedule: value.trim(),
      });
      if (res.data?.error) {
        message.error(res.data.error);
      } else {
        message.success(`Schedule updated: ${scriptName}`);
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to update cron schedule';
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  }, [scriptName, value, onSuccess, onClose]);

  return (
    <Modal
      title={`Edit Cron Schedule — ${scriptName}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      destroyOnClose
    >
      {!confirming ? (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
            Format: <code>minute hour day-of-month month day-of-week</code>
          </div>
          <div style={{ marginBottom: 4, fontSize: 11, color: '#999' }}>
            Examples: <code>0 6 * * *</code> (daily at 06:00) &nbsp;|&nbsp;
            <code>30 */2 * * *</code> (every 2h at :30) &nbsp;|&nbsp;
            <code>0 9 * * 1</code> (Monday 09:00)
          </div>
          <Input
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="e.g. 0 6 * * *"
            style={{ fontFamily: 'monospace', fontSize: 14, marginBottom: 12 }}
            status={value && !isValid ? 'error' : undefined}
          />

          {value && errors.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {errors.map((err, i) => (
                <Alert key={i} message={err} type="error" showIcon style={{ marginBottom: 4, fontSize: 12 }} />
              ))}
            </div>
          )}

          {isValid && humanReadable && (
            <Alert
              message={`This will run ${scriptName} ${humanReadable}`}
              type="info"
              showIcon
              style={{ marginBottom: 12, fontSize: 12 }}
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={!isValid} onClick={handleNext}>
              Next
            </Button>
          </div>
        </>
      ) : (
        <>
          <Alert
            message="Are you sure?"
            description={
              <div>
                <p style={{ margin: '8px 0' }}>
                  You are about to change the cron schedule for <strong>{scriptName}</strong>:
                </p>
                <div style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: 8, borderRadius: 4, marginBottom: 8 }}>
                  <div style={{ color: '#999', textDecoration: 'line-through' }}>{currentSchedule || '(none)'}</div>
                  <div style={{ color: '#1890ff', fontWeight: 500 }}>{value.trim()}</div>
                </div>
                <p style={{ margin: 0 }}>
                  → <strong>{humanReadable}</strong>
                </p>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleBack}>Back</Button>
            <Button type="primary" danger loading={saving} onClick={handleSave}>
              Yes, update schedule
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CronScheduleEditor;
